"use client";

import { useCallback, useState } from "react";
import toaster from "react-hot-toast";

import type {
  SsoProviderId,
  UseSSOAuthResult,
  SSOAuthLogicResult,
} from "@/components/SSOAuthButton/types";

/**
 * Centralized function to clear all SSO-related toasts
 */

const SSO_TOAST_IDS = [
  "custom-toast",
  "error-toast",
  "sso-auth-toast",
  "sso-login-error",
  "sso-register-error",
];
export const clearSSOToasts = () => {
  SSO_TOAST_IDS.forEach((id) => toaster.dismiss(id));
};

export const useSSOState = (): UseSSOAuthResult => {
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [activeProvider, setActiveProvider] = useState<SsoProviderId | null>(null);
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);

  const setSsoLoading = useCallback((isLoading: boolean, provider: SsoProviderId | null = null) => {
    setIsSsoLoading(isLoading);
    setActiveProvider(isLoading ? provider : null);
  }, []);

  // Centralized function to reset all SSO auth state and errors
  const resetAllAuthState = useCallback(
    (authHooks: SSOAuthLogicResult[]) => {
      // Clear all active toasts when switching between forms
      clearSSOToasts();

      // Reset SSO loading state and clear access token
      setIsSsoLoading(false);
      setActiveProvider(null);
      setAccessToken(undefined);

      // Reset API error states for all provided auth hooks
      authHooks.forEach((hook) => {
        if (hook?.resetApiState) {
          hook.resetApiState();
        }
      });
    },
    [setAccessToken]
  );

  return {
    isSsoLoading,
    activeProvider,
    accessToken,
    setSsoLoading,
    setAccessToken,
    resetAllAuthState,
  };
};
